import type { ReactNode } from "react";
import { AudioLines, Film, Image as ImageIcon } from "lucide-react";
import type { AssetType } from "@/lib/api";
import { useAssetIdUrl, useAssetUrl } from "@/hooks/useAssetUrl";
import { cn } from "@/lib/utils";

const ICONS: Record<AssetType, typeof Film> = {
  video: Film,
  audio: AudioLines,
  image: ImageIcon,
};

interface Props {
  type?: AssetType | null;
  url?: string | null;
  storageKey?: string | null;
  assetId?: string | null;
  alt?: string;
  className?: string;
  iconClassName?: string;
  fallback?: ReactNode;
}

export function AssetThumb({
  type = "image",
  url,
  storageKey,
  assetId,
  alt = "",
  className,
  iconClassName,
  fallback,
}: Props) {
  const previewable = type === "image";
  const { url: keyUrl, isLoading: keyLoading } = useAssetUrl(!url && previewable ? storageKey : null);
  const { url: idUrl, isLoading: idLoading } = useAssetIdUrl(
    !url && !storageKey && previewable ? assetId : null
  );
  const resolved = url ?? keyUrl ?? idUrl;

  if (resolved && previewable) {
    return <img src={resolved} alt={alt} className={cn("h-full w-full object-cover", className)} />;
  }

  if (url && type === "video") {
    return (
      <video
        src={url}
        muted
        playsInline
        preload="metadata"
        className={cn("h-full w-full object-cover", className)}
      />
    );
  }

  if (previewable && (keyLoading || idLoading)) {
    return <span className={cn("block h-full w-full animate-pulse bg-studio-panel", className)} />;
  }

  if (fallback !== undefined) {
    return (
      <span className={cn("flex h-full w-full items-center justify-center", className)}>{fallback}</span>
    );
  }

  const Icon = ICONS[type ?? "image"] ?? ImageIcon;
  return (
    <span className={cn("flex h-full w-full items-center justify-center text-studio-muted", className)}>
      <Icon className={cn("h-4 w-4", iconClassName)} />
    </span>
  );
}
